import { Fragment, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link, useNavigate } from "react-router-dom";

import { Dropdown, Icon, Header, Message } from 'semantic-ui-react'

import { JoyyaHeader } from '../../../../components/JoyyaHeader';
import { ClassListItem } from "../../../../components/teacher/ClassListItem";

import { GET_CLASSES } from '../data/GET_CLASSES';
import { CREATE_CLASSRECORD } from '../data/mutation_CREATE_CLASSRECORD';
import { GET_TAGS } from '../../../../queries/tags';

import styles from './TeacherClassRecord.module.css'

import { useBlocks } from '../../../../hooks/useBlocks';
import { useIsAdminLoading } from '../../../../hooks/isAdmin';

// Stable identity so the memos below don't recompute every render for non-admins.
const NO_FILTERS: string[] = [];

const TAGS_STORAGE_KEY = 'allRecords_tagIds';
const TEACHERS_STORAGE_KEY = 'allRecords_teacherIds';

/**
 * localStorage throws outright in some private-browsing modes rather than returning null, and a
 * remembered filter is a convenience — never let it take the page down.
 */
const readStored = (key: string): string[] => {
    try {
        const stored = localStorage.getItem(key);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
};

const writeStored = (key: string, value: string[]) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // The filter still applies for this session, it just won't be remembered next visit.
    }
};

const scoreToLetterGrade = (score: number) => {
    if (score == null || score == 0) return '?';
    if (score >= 90) return 'AA';
    if (score >= 80) return 'A+';
    if (score >= 60) return 'A';
    if (score >= 45) return 'B+';
    if (score >= 35) return 'B';
    if (score >= 25) return 'C';
    return 'D';
}

const TeacherAllRecords = () => {
    const navigate = useNavigate();
    const [createRecord] = useMutation(CREATE_CLASSRECORD);
    // `loading` matters here: useIsAdmin() reports false until `me` resolves, so firing the
    // classes query before then would fetch unfiltered, then refetch — an admin with saved
    // filters would watch the list change under them on every visit.
    const [isAdmin, adminLoading] = useIsAdminLoading();

    const { currentBlockFilter, loading: blocksLoading } = useBlocks();

    const [tagIds, setTagIds] = useState<string[]>(() => readStored(TAGS_STORAGE_KEY));
    const [teacherIds, setTeacherIds] = useState<string[]>(() => readStored(TEACHERS_STORAGE_KEY));

    // Only admins get the filter bar, so only admins get filtered. Someone who loses the admin
    // tag must not be left with an invisible saved filter quietly hiding classes from them.
    const activeTagIds = isAdmin ? tagIds : NO_FILTERS;
    const activeTeacherIds = isAdmin ? teacherIds : NO_FILTERS;

    // Selected tags *replace* the current-block filter rather than ANDing with it, matching the
    // Classes page. The server ANDs every tagId in this string (getCourseIdsMatchingTagFilters),
    // so keeping the block in would return nothing the moment someone picked another block's tag.
    const filters = activeTagIds.length > 0 ? activeTagIds.join(',') : currentBlockFilter;

    const { loading: classesLoading, data: classesData, refetch } = useQuery(GET_CLASSES, {
        skip: adminLoading || blocksLoading || !filters,
        variables: {
            search: '',
            filters
        }
    });
    const loading = adminLoading || blocksLoading || classesLoading;

    const { loading: tagsLoading, error: tagsError, data: tagsData } = useQuery(GET_TAGS, {
        variables: { showPeopleTags: false, showCourseTags: true },
        skip: !isAdmin,
    });

    const allClasses = useMemo(() => classesData?.classes ?? [], [classesData]);

    // Teachers are derived from the classes already on screen rather than a separate query: it
    // costs no extra round trip and keeps the list to people who actually have classes in view.
    const teacherOptions = useMemo(() => {
        const nameById = new Map<string, string>();
        for (const item of allClasses) {
            for (const overseer of item.overseers ?? []) {
                nameById.set(String(overseer.id), overseer.name);
            }
        }
        return Array.from(nameById, ([id, name]) => ({ key: id, text: name, value: id }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [allClasses]);

    // OR across teachers — picking two people shows both of their classes. Deliberately unlike
    // the tag filter above, which is AND because that is what the server's filter string does.
    const visibleClasses = useMemo(() => (
        activeTeacherIds.length === 0
            ? allClasses
            : allClasses.filter((item: any) =>
                (item.overseers ?? []).some((o: any) => activeTeacherIds.includes(String(o.id))))
    ), [allClasses, activeTeacherIds]);

    return (
        <>
            <JoyyaHeader />
            <div className='mobile-frame'>
                <Header as='h2'>
                    <span className={styles.header}>
                        <Icon name='arrow left' className={styles.pointer} onClick={() => navigate(-1)} />
                        All records by class
                    </span>
                </Header>
                {isAdmin && (
                    <div className={styles.filters}>
                        <Dropdown
                            placeholder='Tags'
                            fluid
                            multiple
                            search
                            selection
                            value={tagIds}
                            onChange={(_, { value }) => {
                                const next = value as string[];
                                setTagIds(next);
                                writeStored(TAGS_STORAGE_KEY, next);
                            }}
                            options={
                                tagsLoading || tagsError ? [] :
                                    (tagsData?.tags ?? []).map((p: any) => ({
                                        key: p.id,
                                        text: p.name,
                                        value: `tagId:${p.id}`,
                                    }))
                            }
                        />
                        <Dropdown
                            placeholder='Teachers'
                            fluid
                            multiple
                            search
                            selection
                            value={teacherIds}
                            onChange={(_, { value }) => {
                                const next = value as string[];
                                setTeacherIds(next);
                                writeStored(TEACHERS_STORAGE_KEY, next);
                            }}
                            options={teacherOptions}
                        />
                    </div>
                )}
                {loading && (<Message>Loading records...</Message>)}
                {!loading && allClasses.length > 0 && visibleClasses.length === 0 && (
                    <Message>No classes match the selected teachers.</Message>
                )}
                {visibleClasses.length > 0 && (visibleClasses.map((item: any) => (
                    <Fragment key={item.id}>
                        <Link to={`/classes/${item.id}/records`} style={{ textDecoration: 'none', color: 'inherit' }} key={item.id + '_' + item.time}>
                            <ClassListItem
                                time={item.name}
                                program={'SR'}
                                classLabel={item.classLabel}
                                level={item.level}
                                communityCode={item.SKU.split('-')[0]}
                            />
                        </Link>
                        {/* For each person make a little record card with their scores and completion status */}

                        <div style={{ flexWrap: 'wrap', display: 'flex', gap: '10px' }}>
                            {item.classRecords && item.classRecords.length === 0 && (
                                <>
                                    {item.participants.length === 0 ? (
                                        <p>No records available for this block. No participants</p>
                                    ) : (
                                        <button onClick={async () => {
                                            await Promise.all(
                                                item.participants.map((participant: any) => {
                                                    return createRecord({
                                                        variables: {
                                                            studentUserId: Number(participant.id),
                                                            courseId: Number(item.id),
                                                        }
                                                    });
                                                })
                                            )
                                            await refetch()
                                        }}>
                                            Create records for {item.participants.length} participants
                                        </button>

                                    )}
                                </>
                            )}
                            {item.classRecords && item.classRecords.length > 0 && item.classRecords.map((record: any) => {
                                return record.completionStatus === 'DROPPED' ?
                                    (
                                        <div key={record.id} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px', marginBottom: '10px', maxWidth: '224px', backgroundColor: '#f8d7da' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                {record.user.photoUrl ? (
                                                    <img src={record.user.photoUrl} alt={`${record.user.name}'s profile`} style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                                                ) : (
                                                    <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <span style={{ color: '#fff', fontSize: '24px' }}>{record.user.name.charAt(0)}</span>
                                                    </div>
                                                )}
                                                <div>{record.user.name} ({record.user.identifier})</div>
                                            </div>
                                            <br />
                                            {record.teacherComment && (
                                                <div><strong>Teacher Comment:</strong> {record.teacherComment}</div>
                                            )}
                                        </div>
                                    ) : (
                                        <div key={record.id} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px', marginBottom: '10px', maxWidth: '224px' }}>
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {record.user.photoUrl ? (
                                                        <img src={record.user.photoUrl} alt={`${record.user.name}'s profile`} style={{ width: '50px', height: '50px', borderRadius: '50%' }} />
                                                    ) : (
                                                        <div style={{ width: '50px', height: '50px', borderRadius: '50%', backgroundColor: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <span style={{ color: '#fff', fontSize: '24px' }}>{record.user.name.charAt(0)}</span>
                                                        </div>
                                                    )}
                                                    <div>{record.user.name} ({record.user.identifier})</div>
                                                </div>
                                            </div>
                                            <table style={{ width: '100%', marginTop: '10px', borderCollapse: 'collapse' }}>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}><strong>Capacity</strong></td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{record.endCapacityScore}</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{scoreToLetterGrade(record.endCapacityScore)}</td>
                                                    </tr>
                                                    {/* Yellow */}
                                                    <tr style={{ backgroundColor: '#fff3cd' }}>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}><strong>Confidence</strong></td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{record.confidenceScore}</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{scoreToLetterGrade(record.confidenceScore)}</td>
                                                    </tr>
                                                    {/* Teal */}
                                                    <tr style={{ backgroundColor: '#d1ecf1' }}>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}><strong>Connection</strong></td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{record.connectionScore}</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{scoreToLetterGrade(record.connectionScore)}</td>
                                                    </tr>
                                                    {/* Light red */}
                                                    <tr style={{ backgroundColor: '#f8d7da' }}>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}><strong>Contribution</strong></td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{record.contributionScore}</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{scoreToLetterGrade(record.contributionScore)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}><strong>Attendance</strong></td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{record.attendanceScore}</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{scoreToLetterGrade(record.attendanceScore)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}><strong>Overall</strong></td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{
                                                            (record.connectionScore + record.contributionScore + record.endCapacityScore + record.confidenceScore + record.attendanceScore) / 5
                                                        }</td>
                                                        <td style={{ border: '1px solid #ddd', padding: '8px' }}>{scoreToLetterGrade(
                                                                Math.round((record.connectionScore + record.contributionScore + record.endCapacityScore + record.confidenceScore + record.attendanceScore) / 5)
                                                        )}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            <br />
                                            <div><strong>Teacher Comment:</strong> {record.teacherComment}</div>
                                        </div>
                                    )
                            }

                            )}
                        </div>
                    </Fragment>
                )))}
            </div>
        </>
    );
}

export { TeacherAllRecords }
